import React, { Suspense, lazy, useState, useEffect, useLayoutEffect, useRef, useContext } from "react";
import "../../site/scss/_sport_v2.scss";
import { GlobalContext } from './header/GlobalContext';
import { getCardSelectionState, matchesSelectionVariant } from "./selectionState";
import { formatBoardStartTime, parseBoardStartDate } from "./timeDisplay";
import { getHigherPayoutSide, getSideOddsLabelForStat, HigherPayoutIndicator } from "./higherPayoutIndicator";
import { getCurrentShowOddsValue } from "./FetchStats";
import { isFallbackHeadshotImage } from "./athleteHeadshotPlaceholder";
import AthleteHeadshotImage, { usesCompactHeadshot } from "./AthleteHeadshotImage";
import {
  ArrowPathIcon,
  ChartBarIcon,
  PromoBadgeIcon,
  SelectionDirectionIcon,
  getPromoLabel,
} from "../icons/heroIcons";

const LastFiveStatsModal = lazy(() => import("./modals/LastFiveStatsModal"));

const parseHexColor = (color) => {
  const raw = typeof color === "string" ? color.trim() : "";
  const hex = raw.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];

  if (!hex) {
    return null;
  }

  const normalized = hex.length === 3
    ? hex.split("").map((char) => `${char}${char}`).join("")
    : hex;
  const value = parseInt(normalized, 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const formatRgb = (rgb, fallback = "255, 255, 255") => (
  rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : fallback
);

const rgbToHsl = ({ r, g, b }) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: lightness };
  }

  const delta = max - min;
  const saturation = lightness > 0.5
    ? delta / (2 - max - min)
    : delta / (max + min);
  let hue;

  switch (max) {
    case red:
      hue = (green - blue) / delta + (green < blue ? 6 : 0);
      break;
    case green:
      hue = (blue - red) / delta + 2;
      break;
    default:
      hue = (red - green) / delta + 4;
      break;
  }

  return { h: hue * 60, s: saturation, l: lightness };
};

const hslToRgb = ({ h, s, l }) => {
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const hue = h / 60;
  const x = chroma * (1 - Math.abs((hue % 2) - 1));
  const match = l - chroma / 2;
  let rgbPrime;

  if (hue < 1) rgbPrime = [chroma, x, 0];
  else if (hue < 2) rgbPrime = [x, chroma, 0];
  else if (hue < 3) rgbPrime = [0, chroma, x];
  else if (hue < 4) rgbPrime = [0, x, chroma];
  else if (hue < 5) rgbPrime = [x, 0, chroma];
  else rgbPrime = [chroma, 0, x];

  return {
    r: Math.round((rgbPrime[0] + match) * 255),
    g: Math.round((rgbPrime[1] + match) * 255),
    b: Math.round((rgbPrime[2] + match) * 255),
  };
};

const inferComplementaryRgb = (rgb) => {
  if (!rgb) {
    return null;
  }

  const hsl = rgbToHsl(rgb);
  if (hsl.s < 0.18) {
    return null;
  }

  return hslToRgb({
    h: (hsl.h + 180) % 360,
    s: Math.min(hsl.s * 0.72, 0.62),
    l: Math.min(Math.max(hsl.l, 0.34), 0.62),
  });
};

const hasTeamColor = (color) => (
  typeof color === "string" && /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color.trim())
);

const STATS_SHEET_DISABLED_LEAGUES = new Set(["cs2", "lol", "val", "dota2"]);

const canShowStatsForAthlete = (athlete) => {
  const league = typeof athlete?.league === "string" ? athlete.league.toLowerCase() : "";
  return !STATS_SHEET_DISABLED_LEAGUES.has(league);
};

const getAthleteNavigationKey = (athlete) => (
  String(athlete?.athlete_id || athlete?.uid || athlete?.term_id || athlete?.name || "")
);

const getAdminAthleteTermUrl = (athlete) => {
  const league = String(athlete?.league || "").toLowerCase();
  const termLeague = ["worldcup", "worldcup1h", "worldcup2h"].includes(league) ? "soccer" : league;
  const termId = encodeURIComponent(String(athlete?.term_id || ""));

  return `/wp-admin/term.php?taxonomy=${termLeague}-athlete&tag_ID=${termId}&post_type=${termLeague}_athlete_data`;
};

export const getActiveAltStatKey = (athlete) => {
  const altStats = athlete?.alt_stats && typeof athlete.alt_stats === "object"
    ? athlete.alt_stats
    : {};
  const selectionState = getCardSelectionState(
    athlete?.stat_key,
    athlete?.promo,
    athlete?.easy,
    athlete?.hard
  );
  const canonicalStatKey = selectionState.statKey;
  const demonStatKey = typeof canonicalStatKey === "string"
    ? canonicalStatKey.replace(/^projected_/, "demon_")
    : canonicalStatKey;
  const goblinStatKey = typeof canonicalStatKey === "string"
    ? canonicalStatKey.replace(/^projected_/, "goblin_")
    : canonicalStatKey;

  if (selectionState.hard && Object.prototype.hasOwnProperty.call(altStats, demonStatKey)) {
    return demonStatKey;
  }

  if (selectionState.easy && Object.prototype.hasOwnProperty.call(altStats, goblinStatKey)) {
    return goblinStatKey;
  }

  if (Object.prototype.hasOwnProperty.call(altStats, athlete?.stat_key)) {
    return athlete.stat_key;
  }

  if (Object.prototype.hasOwnProperty.call(altStats, canonicalStatKey)) {
    return canonicalStatKey;
  }

  return Object.keys(altStats)[0] || canonicalStatKey;
};


const AthleteCard = (props) => {
  const previewOnly = props.previewOnly === true;
  const initialSelectionState = getCardSelectionState(
    props.athlete.stat_key,
    props.athlete.promo,
    props.athlete.easy,
    props.athlete.hard
  );
  const [countdown, setCountdown] = useState("");
  const [activeStatKey, setActiveStatKey] = useState(getActiveAltStatKey(props.athlete));
  const [statKey, setStatKey] = useState(initialSelectionState.statKey);
  const [statValue, setStatValue] = useState(props.athlete.stat);
  const [athleteEasy, setAthleteEasy] = useState(initialSelectionState.easy);
  const [athleteHard, setAthleteHard] = useState(initialSelectionState.hard);
  const [showStatSheet, setShowStatSheet] = useState(false);
  const [statSheetIndex, setStatSheetIndex] = useState(props.index || 0);
  const [timeDisplay, setTimeDisplay] = useState("");
  const intervalRef = useRef(null);
  const [promo, setPromo] = useState(initialSelectionState.promoToken);
  const { isH2H, isAdmin } = useContext(GlobalContext);
  const { athlete, onClick, selected, index } = props;
  const showOdds = getCurrentShowOddsValue() === "1";
  const moreOddsLabel = getSideOddsLabelForStat(athlete, "more", statKey);
  const lessOddsLabel = getSideOddsLabelForStat(athlete, "less", statKey);
  const moreButtonLabel = showOdds ? (moreOddsLabel || "N/A") : "More";
  const lessButtonLabel = showOdds ? (lessOddsLabel || "N/A") : "Less";
  const higherPayoutSide = getHigherPayoutSide(athlete, statKey);
  const statsSheetAthletes = Array.isArray(props.athletes)
    ? props.athletes.filter(canShowStatsForAthlete)
    : [athlete].filter(canShowStatsForAthlete);
  const resolveCurrentStatSheetIndex = () => {
    const currentKey = getAthleteNavigationKey(athlete);
    const currentIndex = statsSheetAthletes.findIndex((candidate) => (
      candidate === athlete || getAthleteNavigationKey(candidate) === currentKey
    ));

    if (currentIndex >= 0) {
      return currentIndex;
    }

    return Math.min(Math.max(index || 0, 0), Math.max(statsSheetAthletes.length - 1, 0));
  };
  const clampedStatSheetIndex = statsSheetAthletes.length > 0
    ? Math.min(Math.max(statSheetIndex, 0), statsSheetAthletes.length - 1)
    : 0;
  const statSheetAthlete = statsSheetAthletes[clampedStatSheetIndex] || athlete;
  const previousStatSheetAthlete = clampedStatSheetIndex > 0
    ? statsSheetAthletes[clampedStatSheetIndex - 1]
    : null;
  const nextStatSheetAthlete = clampedStatSheetIndex + 1 < statsSheetAthletes.length
    ? statsSheetAthletes[clampedStatSheetIndex + 1]
    : null;
  const shrk = typeof document !== "undefined" && document.getElementById("shrk")?.value === "1";
  const locationTxt = athlete.location == 'away' ? '@' : 'vs. ';
  const canShowStatSheet = canShowStatsForAthlete(athlete);
  const leagueKey = typeof athlete.league === "string" ? athlete.league.toLowerCase() : "";
  const useCompactHeadshot = usesCompactHeadshot(leagueKey);
  const hasHeadshotImage = !isFallbackHeadshotImage(athlete.image);
  const athletePosition = typeof athlete.position === "string" ? athlete.position.trim() : "";
  const displayPosition = athletePosition && athletePosition !== "_" && athletePosition.toLowerCase() !== "player" ? athletePosition : "";
  const athleteTeam = typeof athlete.team_short === "string" ? athlete.team_short.trim() : "";
  const displayTeam = athleteTeam && athleteTeam !== "_" ? athleteTeam : "";
  const normalizedLeague = typeof athlete.league === "string" ? athlete.league.trim().toLowerCase() : "";
  const displayLeague = normalizedLeague.replace(/_/g, " ").toUpperCase();
  const teamColorRgb = parseHexColor(athlete.team_color);
  const teamColorTwoRgb = parseHexColor(athlete.team_color_two);
  const secondaryColorRgb = teamColorTwoRgb || inferComplementaryRgb(teamColorRgb);
  const headshotStyle = {
    "--athlete-headshot-gradient-rgb": formatRgb(teamColorRgb),
    "--athlete-headshot-gradient-alpha": hasTeamColor(athlete.team_color) ? 0.68 : 0.36,
    "--athlete-headshot-gradient-secondary-rgb": formatRgb(secondaryColorRgb),
    "--athlete-headshot-gradient-secondary-alpha": teamColorTwoRgb ? 0.38 : (secondaryColorRgb ? 0.22 : 0),
  };
  const metaPillLabel = [displayTeam || displayLeague, displayPosition].filter(Boolean).join(" • ");
  const metaPillStyle = teamColorRgb ? {
    "--athlete-meta-pill-border": `rgb(${formatRgb(teamColorRgb)})`,
  } : {};
  const isPromo = promo === "promo";
  let moreSelected = false;
  let lessSelected = false;
  let moreLess = (!athleteEasy && !athleteHard && !isPromo);
  const loggedIn = document.body.classList.contains('logged-in');
  let athleteMatchSearch = selected.findIndex((x) =>
    matchesSelectionVariant(x, athlete.athlete_id, statKey, promo, athleteEasy, athleteHard)
  );
  let athleteMatch = athleteMatchSearch > -1;
  if (athleteMatch) {
    let matching = selected[athleteMatchSearch];
    let moreMatch = matching.selected == "more";
    moreSelected = athleteMatch && moreMatch;
    lessSelected = athleteMatch && !moreMatch;
  }

  useEffect(() => {
    updateCountdown();
    intervalRef.current = setInterval(updateCountdown, 1000);

    setTimeDisplay(formatBoardStartTime(props.athlete.start_time_obj, {
      weekdayPeriod: true,
    }));

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [props.athlete.start_time_obj, props.athlete.promo]);

  // A card can retain a locally swapped alternate market while React reuses
  // it for a refreshed board row. Synchronize prop-driven market state before
  // paint so the previous market can never flash under the newly selected tab.
  useLayoutEffect(() => {
    const nextSelectionState = getCardSelectionState(
      props.athlete.stat_key,
      props.athlete.promo,
      props.athlete.easy,
      props.athlete.hard
    );

    setActiveStatKey(getActiveAltStatKey(props.athlete));
    setStatKey(nextSelectionState.statKey);
    setStatValue(props.athlete.stat);
    setAthleteEasy(nextSelectionState.easy);
    setAthleteHard(nextSelectionState.hard);
    setPromo(nextSelectionState.promoToken);
  }, [
    props.athlete.athlete_id,
    props.athlete.stat,
    props.athlete.stat_key,
    props.athlete.easy,
    props.athlete.hard,
    props.athlete.promo,
  ]);

  const updateCountdown = () => {
    const startTimeObj = parseBoardStartDate(props.athlete.start_time_obj);
    if (!startTimeObj) {
      setCountdown("");
      clearInterval(intervalRef.current);
      return;
    }

    const currentTime = new Date();
    const countdown = startTimeObj.getTime() - currentTime.getTime();

    // Check if the event starts within 2 hours (7200000 milliseconds) or if promo is true
    if (countdown < 0 || (!props.athlete.promo && countdown > 7200000)) {
      setCountdown(""); // Clear countdown or set to a default message
      clearInterval(intervalRef.current);
      return;
    }

    const hours = Math.floor((countdown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((countdown % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((countdown % (1000 * 60)) / 1000);
    let countdownString = '';
    if (hours > 0) {
        countdownString += `${hours}h `;
    }
    countdownString += hours > 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
    setCountdown(countdownString);
  };

  const handleInfoClick = (type, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (typeof props.onOpenInfoModal === "function") {
      props.onOpenInfoModal(type);
      return;
    }

    if (type === "easy") {
      $("#easy-modal").modal("show");
    } else if (type === "hard") {
      $("#hard-modal").modal("show");
    } else {
      $("#promo-modal").modal("show");
    }
  };

  const handleStatSwap = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const altStats = props.athlete.alt_stats && typeof props.athlete.alt_stats === "object"
      ? props.athlete.alt_stats
      : {};
    const keys = Object.keys(altStats);

    if (keys.length <= 1) {
      return;
    }

    const currentActiveKey = keys.includes(activeStatKey)
      ? activeStatKey
      : getActiveAltStatKey(props.athlete);
    const index = keys.indexOf(currentActiveKey);
    const nextIndex = index >= 0 && index + 1 < keys.length ? index + 1 : 0;
    const nextRawKey = keys[nextIndex];
    const nextSelectionState = getCardSelectionState(nextRawKey, props.athlete.promo);

    setActiveStatKey(nextRawKey);
    setStatKey(nextSelectionState.statKey);
    setStatValue(altStats[nextRawKey]);
    setAthleteEasy(nextSelectionState.easy);
    setAthleteHard(nextSelectionState.hard);
    setPromo(nextSelectionState.promoToken);

    if (typeof props.onSwapStat === "function") {
      props.onSwapStat({
        athleteId: props.athlete.athlete_id,
        previousStatKey: statKey,
        previousPromoToken: promo,
        previousEasy: athleteEasy,
        previousHard: athleteHard,
        nextStatKey: nextSelectionState.statKey,
        nextStatValue: altStats[nextRawKey],
        nextPromoToken: nextSelectionState.promoToken,
        nextEasy: nextSelectionState.easy,
        nextHard: nextSelectionState.hard,
      });
    }
  };

  const closeModal = () => {
    setShowStatSheet(false);
  };

  const openStatSheet = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (previewOnly || !canShowStatSheet) {
      return;
    }

    setStatSheetIndex(resolveCurrentStatSheetIndex());
    setShowStatSheet(true);
  };

  const rawCurrentStat = athlete.current_stat;
  const hasCurrentStatValue =
    rawCurrentStat !== null &&
    rawCurrentStat !== undefined &&
    String(rawCurrentStat).trim() !== "";
  const currentStatValue = hasCurrentStatValue ? Number(rawCurrentStat) : null;
  const hasCurrentStat =
    Boolean(athlete.is_live) &&
    hasCurrentStatValue &&
    Number.isFinite(currentStatValue);

  const navigateStatSheet = (direction) => {
    setStatSheetIndex((currentIndex) => {
      const nextIndex = currentIndex + direction;
      return Math.min(Math.max(nextIndex, 0), Math.max(statsSheetAthletes.length - 1, 0));
    });
  };

  const handleHeadshotKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    openStatSheet();
  };

  return (
    <div
      aria-busy={previewOnly ? "true" : undefined}
      className={
        "athlete-card flex-fill" +
        (moreSelected || lessSelected ? " b-selected" : "") +
        (useCompactHeadshot ? " athlete-card--compact-headshot" : "") +
        (!hasHeadshotImage ? " athlete-card--fallback-headshot" : "") +
        (athlete.is_live ? " athlete-card--live" : "") +
        (athlete._boardRefreshState === "changed" ? " athlete-card--line-changed" : "")
      }
    >
      {athlete.is_live && (
        <span className="athlete-card__live-badge">
          <span className="athlete-card__live-badge-dot" aria-hidden="true" />
          Live
        </span>
      )}
      <div className="outer-mf">
        <div className="more-info">

        { canShowStatSheet && !athlete.is_live && (
          <>
            <label htmlFor={`graph-button-${athlete.athlete_id}`} className="sr-only">View Stats</label>
            <button id={`graph-button-${athlete.athlete_id}`} className="graph" type="button" disabled={previewOnly} onClick={previewOnly ? undefined : openStatSheet} aria-label="View Stats">
              <ChartBarIcon aria-hidden="true" className="ww-card-icon" />
            </button>

            {showStatSheet && !previewOnly && (
              <Suspense fallback={null}>
                <LastFiveStatsModal
                  termId={statSheetAthlete.term_id}
                  athlete={statSheetAthlete}
                  closeModal={closeModal}
                  nextAthlete={nextStatSheetAthlete}
                  onNavigateNext={nextStatSheetAthlete ? () => navigateStatSheet(1) : undefined}
                  onNavigatePrevious={previousStatSheetAthlete ? () => navigateStatSheet(-1) : undefined}
                  onSelectProp={onClick}
                  previousAthlete={previousStatSheetAthlete}
                  selected={selected}
                />
              </Suspense>
            )}

          </>
        )}

          {isPromo && (
            <>
              <button className="promo" type="button" onClick={(event) => handleInfoClick("promo", event)} title={getPromoLabel("promo")}>
                <PromoBadgeIcon className="ww-promo-icon--button" token="promo" />
              </button>
            </>
          )}

          {athleteHard && (
            <>
              <button className="promo" type="button" onClick={(event) => handleInfoClick("hard", event)} title={getPromoLabel("hard")}>
                <PromoBadgeIcon className="ww-promo-icon--button" token="hard" />
              </button>
            </>
          )}

          {athleteEasy&& (
            <>
              <button className="promo" type="button" onClick={(event) => handleInfoClick("easy", event)} title={getPromoLabel("easy")}>
                <PromoBadgeIcon className="ww-promo-icon--button" token="easy" />
              </button>
            </>
          )}
        </div>
      </div>

      {isAdmin ? (
        <a
          href={`/wp-admin/admin.php?page=prop-check-admin&athlete_name=${encodeURIComponent(athlete.name)}&league=${athlete.league.toUpperCase()}&prop_type=${encodeURIComponent(statKey)}`}
          target="_blank"
        >
          <div className="athlete-headshot" style={headshotStyle}>
            <AthleteHeadshotImage alt={athlete.name} faceNormalization={athlete.image_face} placeholderWhileLoading={props.showHeadshotPlaceholderWhileLoading} priority={props.loadHeadshotEagerly} src={athlete.image} />
          </div>
        </a>
      ) : (
        <div
          className="athlete-headshot"
          style={headshotStyle}
          onClick={previewOnly ? undefined : openStatSheet}
          onKeyDown={previewOnly ? undefined : handleHeadshotKeyDown}
          role={canShowStatSheet && !previewOnly ? "button" : undefined}
          tabIndex={canShowStatSheet && !previewOnly ? 0 : undefined}
          aria-label={canShowStatSheet && !previewOnly ? `View recent stats for ${athlete.name}` : undefined}
        >
          <AthleteHeadshotImage alt={athlete.name} faceNormalization={athlete.image_face} placeholderWhileLoading={props.showHeadshotPlaceholderWhileLoading} priority={props.loadHeadshotEagerly} src={athlete.image} />
        </div>
      )}

      {metaPillLabel && (
        <div className="athlete-meta-pill" style={metaPillStyle} title={metaPillLabel}>
          {metaPillLabel}
        </div>
      )}

      {isAdmin ? (
        <h3 className="name">
          <a target="_blank" href={getAdminAthleteTermUrl(athlete)}>
            {athlete.name}
          </a>
        </h3>
      ) : (
        <h3 className="name">{athlete.name}</h3>
      )}

      {!props.hideEventContext && (
        <div className="card-txt">
          {locationTxt} {athlete.opponent_short}{countdown ? <span className="countdown"> · {countdown}</span> : timeDisplay && <span className="start-time"> · {timeDisplay}</span>}
        </div>
      )}

      <div className={
          "avg-stat mt-1 mb-2" +
          (moreSelected || lessSelected ? " selected" : "") +
          (athlete._boardRefreshState === "changed" ? " avg-stat--line-changed" : "")
        }
      >
        <span className="avg-stat__content">
          {!shrk && Object.keys(athlete.alt_stats || {}).length > 1 && !isH2H && (
            <button
              type="button"
              className="btn-swap"
              disabled={previewOnly}
              onClick={previewOnly ? undefined : handleStatSwap}
              aria-label={`Swap stats for ${athlete.name}`}
              title="Swap stats"
              style={{ "--swap-intro-delay": `${Math.min(index, 7) * 45}ms` }}
            >
              <ArrowPathIcon aria-hidden="true" className="ww-inline-icon" />
            </button>
          )}
          {athlete.og_value && (
            <span className="og-value">{athlete.og_value}</span>
          )}
          {hasCurrentStat && <span className="avg-stat__current">{currentStatValue} /</span>}
          <span className="avg-stat__value">{statValue}</span>
          {/* <span className={["avg-stat__value", loggedIn || athlete.og_value ? "" : "logged-out"].filter(Boolean).join(" ")}>{statValue}</span> */}
          <span className="avg-stat__label" title={athlete.stat_display}>{athlete.stat_display}</span>
        </span>
      </div>
      <div className="row ml-btns">
        <div className={(moreLess ? "col-6" : "col-12") + " border-middle ml-btn" + (moreSelected ? " selected" : "")}>
          <label htmlFor={`more-button-${athlete.athlete_id}`} className="sr-only">Select More</label>
          <button id={`more-button-${athlete.athlete_id}`} disabled={previewOnly} onClick={previewOnly ? undefined : onClick} value={athlete.athlete_id} data-promo={promo} data-prop="more" data-value={statValue} data-stat={statKey} className={"athlete-btn" + (moreSelected ? " selected" : "")}>
            <span>
              {higherPayoutSide === "more" ? (
                <HigherPayoutIndicator side="more" />
              ) : (
                <SelectionDirectionIcon className="ww-inline-icon mr-1" direction="more" />
              )}
              {moreButtonLabel}
            </span>
          </button>
        </div>

        {moreLess && (
          <div className={"col-6 ml-btn" + (lessSelected ? " selected" : "")}>
            <label htmlFor={`less-button-${athlete.athlete_id}`} className="sr-only">Select Less</label>
            <button id={`less-button-${athlete.athlete_id}`} disabled={previewOnly} onClick={previewOnly ? undefined : onClick} value={athlete.athlete_id} data-promo={promo} data-value={statValue} data-prop="less" data-stat={statKey} className={"athlete-btn" + (lessSelected ? " selected" : "")}>
              <span>
                {higherPayoutSide === "less" ? (
                  <HigherPayoutIndicator side="less" />
                ) : (
                  <SelectionDirectionIcon className="ww-inline-icon mr-1" direction="less" />
                )}
                {lessButtonLabel}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AthleteCard;
